import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import { LanguageProvider, useLang } from "./lib/LanguageContext";
import Navbar from "./components/Navbar";
import Hero from "./components/sections/Hero";
import About from "./components/sections/About";
import WhyMatters from "./components/sections/WhyMatters";
import Offerings from "./components/sections/Offerings";
import Audience from "./components/sections/Audience";
import VisionMission from "./components/sections/VisionMission";
import Identity from "./components/sections/Identity";
import Collaboration from "./components/sections/Collaboration";
import LaunchingSoon from "./components/sections/LaunchingSoon";
import FinalCTA from "./components/sections/FinalCTA";
import Footer from "./components/Footer";
import { Toaster } from "sonner";

function useScrollReveal() {
    // Because language is now route-based, Shell fully remounts on navigation.
    // We only need a one-time IntersectionObserver setup per mount — no stale
    // observer / remount races anymore. This replaces the in-page toggle logic.
    useEffect(() => {
        const els = document.querySelectorAll(".sau-reveal");
        if (els.length === 0) return;
        if (!("IntersectionObserver" in window)) {
            els.forEach((el) => el.classList.add("is-visible"));
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
}

function Shell() {
    const { dir, isRTL } = useLang();
    useScrollReveal();

    return (
        <div
            dir={dir}
            className={`min-h-screen w-full max-w-full overflow-x-hidden sau-bg text-[color:var(--sau-text)] ${isRTL ? "font-arabic" : ""}`}
            data-testid="sau-app-root"
        >
            <div className="relative w-full max-w-full overflow-x-hidden">
                <Navbar />
                <main className="w-full max-w-full overflow-x-hidden">
                    <Hero />
                    <About />
                    <WhyMatters />
                    <Offerings />
                    <Audience />
                    <VisionMission />
                    <Identity />
                    <Collaboration />
                    <LaunchingSoon />
                    <FinalCTA />
                </main>
                <Footer />
            </div>
            <Toaster
                position={isRTL ? "bottom-left" : "bottom-right"}
                theme="dark"
                richColors
                toastOptions={{
                    style: {
                        background: "#11243A",
                        border: "1px solid #1E344D",
                        color: "#F7FAFC",
                    },
                }}
            />
        </div>
    );
}

// Each route mounts its own LanguageProvider with a fixed language, so there is
// no in-page language swapping anywhere. This is the core of the stability fix.
function Page({ lang }) {
    return (
        <LanguageProvider lang={lang}>
            <Shell />
        </LanguageProvider>
    );
}

function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route path="/" element={<Page lang="en" />} />
                <Route path="/ar" element={<Page lang="ar" />} />
                {/* Any unknown route falls back to the English landing page */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}

export default App;
